import { Suspense, lazy, useEffect, useRef } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { ROOT_PATHS, Tabbar } from './components/Tabbar';
import { Author } from './pages/Author';
import { Detail } from './pages/Detail';
import { CollectionDetail, Favorites } from './pages/Favorites';
import { Home } from './pages/Home';
import { Leaderboard } from './pages/Leaderboard';
import { Profile } from './pages/Profile';
import { Search } from './pages/Search';
import { Submit } from './pages/Submit';
import { Support } from './pages/Support';
import { Welcome, hasOnboarded } from './pages/Welcome';
import { startParam } from './lib/telegram';

/**
 * The component bench. Lazy-loaded into its own chunk so it costs the production bundle
 * nothing, but still reachable in a preview build — which is what the screenshot loop
 * shoots against.
 */
const KitchenSink = lazy(() =>
  import('./pages/KitchenSink').then((m) => ({ default: m.KitchenSink })),
);

export default function App() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  /** StrictMode runs an effect twice in dev; this one pushes, so it must not run twice. */
  const routed = useRef(false);

  /**
   * Deep link: `t.me/<bot>?startapp=prompt_<id>` opens straight on that prompt.
   *
   * **Home is seeded behind it, then the target is pushed.** Every branch here used
   * `{ replace: true }`, which overwrites the single history entry the session has — so the
   * sub-screen was the *bottom* of the stack, `Screen`'s back handler (`navigate(-1)`) had
   * nowhere to go, and Telegram's back arrow did nothing. On exactly the entry path the
   * channel's 408 posts use, and the one every share link produces.
   *
   * Two calls rather than one: the first replaces the entry with Home so Back has a destination
   * inside the app, the second pushes the deep-linked screen. At the root the arrow is hidden
   * (`Screen root` passes `null`), so Back there closes the app, which is Telegram's own default.
   *
   * `start_param` is fixed for the session, so this runs once.
   */
  useEffect(() => {
    if (routed.current) return;
    const param = startParam();
    if (!param) return;
    const prompt = /^prompt[_-](.+)$/.exec(param)?.[1];
    const to = prompt
      ? `/prompt/${prompt}`
      : param === 'leaderboard'
        ? '/leaderboard'
        : param === 'submit'
          ? '/submit'
          : null;
    if (!to) return;
    routed.current = true;
    navigate('/', { replace: true });
    navigate(to);
  }, []);

  return (
    <>
      <Routes>
        <Route path="/" element={hasOnboarded() ? <Home /> : <Navigate to="/welcome" replace />} />
        <Route path="/welcome" element={<Welcome />} />
        <Route path="/search" element={<Search />} />
        <Route path="/favorites" element={<Favorites />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/prompt/:id" element={<Detail />} />
        <Route path="/collection/:id" element={<CollectionDetail />} />
        <Route path="/author/:key" element={<Author />} />
        <Route path="/leaderboard" element={<Leaderboard />} />
        <Route path="/submit" element={<Submit />} />
        <Route path="/support" element={<Support />} />
        <Route
          path="/dev/components"
          element={
            <Suspense fallback={null}>
              <KitchenSink />
            </Suspense>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      {ROOT_PATHS.has(pathname) && <Tabbar />}
    </>
  );
}
